import type { useTranslations } from 'next-intl';

type TFunction = ReturnType<typeof useTranslations>;

type ButtonSelectorProps = {
  options: string[];
  onSelect: (selected: string) => void;
  selectedOption: string | null;
  t: TFunction;
  error?: boolean;
};

const ButtonSelector: React.FC<ButtonSelectorProps> = ({ options, onSelect, selectedOption, error, t }) => {
  // Helper function to determine the outer border style based on error
  const getOuterBorderClass = () => {
    if (error && selectedOption === null) {
      return 'border-[#D04251]'; // Red border if no option is selected and there is an error
    }
    return 'border-[#BBBDD1]'; // Default outer border
  };

  // Helper function to determine the styles for each option
  const getOptionClass = (option: string) => {
    if (selectedOption === option) {
      return 'bg-[#CCDBFF] text-black rounded-[3px] z-10'; // Selected option styles
    }
    return 'bg-white text-black'; // Default unselected styles
  };

  // Adjust spacing to avoid cutting off selected options
  const getContainerClass = (index: number) => {
    if (index === 0) {
      return 'rounded-l-md';
    } // First option
    if (index === options.length - 1) {
      return 'rounded-r-md';
    } // Last option
    return ''; // Middle options
  };

  return (
    <div
      className={`flex h-[48px] items-center rounded-[4px] border ${getOuterBorderClass()}`}
    >
      {options.map((option, index) => (
        <div key={option} className="flex h-full flex-1 items-center py-[3px]">
          {index > 0 && (
            <div className="h-6 w-px bg-[#D4D6EC]" />
          )}
          <div
            role="button"
            tabIndex={0}
            className={`
              relative m-[3px] flex h-full flex-1 cursor-pointer items-center justify-center text-[16px]
              ${getOptionClass(option)}
              ${getContainerClass(index)}
              focus:outline-hidden focus-visible:ring-2 focus-visible:ring-[#464862]
            `}
            onClick={() => onSelect(option)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onSelect(option); // Select option with Enter or Space
              }
            }}
          >
            {t(option)}
          </div>
        </div>
      ))}
    </div>
  );
};

export { ButtonSelector };
